/**
 * Shareable Design Links — Stateless Collaboration
 *
 * Encodes the current design state into a compact URL fragment that
 * anyone can open to get a copy of the design. No server round-trip
 * required for the free tier — everything lives in the URL hash.
 *
 * URL format: stencil.cloud/#/d/v1.<compressed-base64url>
 *
 * Pipeline:
 *   State object → strip defaults → JSON.stringify → deflate-raw → base64url → URL
 *
 * The `ref` field embeds the sharer's user ID (or anonymous fingerprint)
 * for future referral tracking.
 *
 * @module share
 */

import { createDesignState, DEFAULT_SIZE } from './design-state.js';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const SHARE_VERSION = 'v1';
const SHARE_PREFIX = '#/d/';
const MAX_URL_WARN = 1500;   // chars — show warning above this
const MAX_URL_HARD = 8000;   // chars — refuse to generate above this

// Anonymous referral ID — generated once per browser, persisted in localStorage
const ANON_REF_KEY = 'stencil-anon-ref';

// ─────────────────────────────────────────────────────────────────────────────
// ANONYMOUS REFERRAL ID
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Get or create a stable anonymous referral ID for this browser.
 * Used when the user isn't logged in — provides a consistent identifier
 * for future referral attribution without requiring an account.
 *
 * Format: 12-char alphanumeric string (collision-resistant enough for referrals)
 */
function getAnonRefId() {
  let id = localStorage.getItem(ANON_REF_KEY);
  if (!id) {
    const arr = new Uint8Array(9); // 9 bytes → 12 base64 chars
    crypto.getRandomValues(arr);
    id = btoa(String.fromCharCode(...arr))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
    localStorage.setItem(ANON_REF_KEY, id);
  }
  return id;
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPRESSION (CompressionStream API — native, zero dependencies)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Compress a string using deflate-raw and return base64url-encoded result.
 * Uses the native CompressionStream API (supported in all modern browsers).
 */
async function compressToBase64url(str) {
  const input = new TextEncoder().encode(str);

  // Use CompressionStream if available (Chrome 80+, Firefox 113+, Safari 16.4+)
  if (typeof CompressionStream !== 'undefined') {
    const cs = new CompressionStream('deflate-raw');
    const writer = cs.writable.getWriter();
    writer.write(input);
    writer.close();

    const chunks = [];
    const reader = cs.readable.getReader();
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
    }

    const totalLen = chunks.reduce((s, c) => s + c.length, 0);
    const compressed = new Uint8Array(totalLen);
    let offset = 0;
    for (const chunk of chunks) {
      compressed.set(chunk, offset);
      offset += chunk.length;
    }

    return uint8ToBase64url(compressed);
  }

  // Fallback: no compression, just base64url the raw JSON
  // (still works, just longer URLs)
  return uint8ToBase64url(input);
}

/**
 * Decompress a base64url string back to the original string.
 */
async function decompressFromBase64url(encoded) {
  const compressed = base64urlToUint8(encoded);

  if (typeof DecompressionStream !== 'undefined') {
    const ds = new DecompressionStream('deflate-raw');
    const writer = ds.writable.getWriter();
    writer.write(compressed);
    writer.close();

    const chunks = [];
    const reader = ds.readable.getReader();
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
    }

    const totalLen = chunks.reduce((s, c) => s + c.length, 0);
    const decompressed = new Uint8Array(totalLen);
    let offset = 0;
    for (const chunk of chunks) {
      decompressed.set(chunk, offset);
      offset += chunk.length;
    }

    return new TextDecoder().decode(decompressed);
  }

  // Fallback: uncompressed payload
  return new TextDecoder().decode(compressed);
}

// ─────────────────────────────────────────────────────────────────────────────
// BASE64URL ENCODING (RFC 4648 §5 — URL-safe, no padding)
// ─────────────────────────────────────────────────────────────────────────────

function uint8ToBase64url(bytes) {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function base64urlToUint8(str) {
  // Restore standard base64
  let b64 = str.replace(/-/g, '+').replace(/_/g, '/');
  // Add padding
  while (b64.length % 4 !== 0) b64 += '=';
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

// ─────────────────────────────────────────────────────────────────────────────
// DEFAULT STRIPPING — only encode non-default properties to minimize size
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULTS = createDesignState();

/**
 * Strip default values from a state object to minimize payload size.
 * Only keeps properties that differ from the default design state.
 */
function stripDefaults(state) {
  const stripped = {};

  // Always include — these define the design
  stripped.t = state.template;   // template key
  stripped.s = state.scheme;     // scheme name (string)
  stripped.z = state.size;       // size key

  // Overlays — only if non-default
  if (state.overlays) {
    const ov = {};
    const la = state.overlays.lineArt;
    if (la && la.type !== 'none') {
      ov.la = { t: la.type, d: la.density, o: la.opacity, s: la.seed };
    }
    const la2 = state.overlays.lineArt2;
    if (la2 && la2.type !== 'none') {
      ov.la2 = { t: la2.type, d: la2.density, o: la2.opacity, s: la2.seed };
    }
    const gr = state.overlays.grain;
    if (gr && gr.enabled) {
      ov.gr = { d: gr.density, o: gr.opacity };
    }
    if (Object.keys(ov).length > 0) stripped.ov = ov;
  }

  // Text edits — only non-empty values
  if (state.textEdits) {
    const edits = {};
    for (const [id, text] of Object.entries(state.textEdits)) {
      if (text && text.trim()) edits[id] = text;
    }
    if (Object.keys(edits).length > 0) stripped.tx = edits;
  }

  // Text style overrides — only if any exist
  if (state.textStyleOverrides && Object.keys(state.textStyleOverrides).length > 0) {
    stripped.ts = state.textStyleOverrides;
  }

  // Group alignment overrides
  if (state.groupAlignOverrides && Object.keys(state.groupAlignOverrides).length > 0) {
    stripped.ga = state.groupAlignOverrides;
  }

  // Wordmark — only if set
  if (state.wordmark?.text) {
    stripped.wm = {
      t: state.wordmark.text,
      f: state.wordmark.font,
      c: state.wordmark.color,
      b: state.wordmark.bold || false,
      i: state.wordmark.italic || false,
      u: state.wordmark.uppercase || false,
      a: state.wordmark.accent,
    };
  }

  // Mangle seed — only if non-zero
  if (state.mangleSeed) {
    stripped.mg = state.mangleSeed;
    if (state.mangleParams) stripped.mp = state.mangleParams;
  }

  // Date and time
  if (state.eventDateISO) stripped.dt = state.eventDateISO;
  if (state.dateFormat && state.dateFormat !== 'dayMonth') stripped.df = state.dateFormat;
  if (state.eventTime) stripped.et = state.eventTime;

  // User-created text objects
  if (state.userTextObjects?.length > 0) {
    stripped.uo = state.userTextObjects;
  }

  // Referral ID — sharer's user ID or anonymous fingerprint
  stripped.ref = state.ref;

  return stripped;
}

/**
 * Expand a stripped state back to the full format for hydration.
 */
function expandDefaults(stripped) {
  const state = {};

  state.template = stripped.t || 'generic-event';
  state.scheme = stripped.s || 'dark';
  state.size = stripped.z || DEFAULT_SIZE;

  // Overlays
  state.overlays = {
    lineArt: { type: 'none', density: 50, opacity: 30, seed: 42 },
    lineArt2: { type: 'none', density: 30, opacity: 20, seed: 137 },
    grain: { enabled: false, density: 40, opacity: 15 },
  };
  if (stripped.ov) {
    if (stripped.ov.la) {
      state.overlays.lineArt = {
        type: stripped.ov.la.t,
        density: stripped.ov.la.d,
        opacity: stripped.ov.la.o,
        seed: stripped.ov.la.s || 42,
      };
    }
    if (stripped.ov.la2) {
      state.overlays.lineArt2 = {
        type: stripped.ov.la2.t,
        density: stripped.ov.la2.d,
        opacity: stripped.ov.la2.o,
        seed: stripped.ov.la2.s || 137,
      };
    }
    if (stripped.ov.gr) {
      state.overlays.grain = {
        enabled: true,
        density: stripped.ov.gr.d,
        opacity: stripped.ov.gr.o,
      };
    }
  }

  // Text edits
  if (stripped.tx) state.textEdits = stripped.tx;

  // Text style overrides
  if (stripped.ts) state.textStyleOverrides = stripped.ts;

  // Group alignment overrides
  if (stripped.ga) state.groupAlignOverrides = stripped.ga;

  // Wordmark
  if (stripped.wm) {
    state.wordmark = {
      text: stripped.wm.t,
      font: stripped.wm.f,
      color: stripped.wm.c,
      bold: stripped.wm.b || false,
      italic: stripped.wm.i || false,
      uppercase: stripped.wm.u || false,
      accent: stripped.wm.a,
    };
  }

  // Mangle
  if (stripped.mg) {
    state.mangleSeed = stripped.mg;
    state.mangleParams = stripped.mp || { spread: 50, angle: 0, colorShift: 0, styleShift: 0 };
  }

  // Date / time
  if (stripped.dt) state.eventDateISO = stripped.dt;
  if (stripped.df) state.dateFormat = stripped.df;
  if (stripped.et) state.eventTime = stripped.et;

  // User-created text objects
  if (stripped.uo) state.userTextObjects = stripped.uo;

  // Referral
  if (stripped.ref) state.ref = stripped.ref;

  return state;
}

// ─────────────────────────────────────────────────────────────────────────────
// TEXT SANITIZATION — defense-in-depth against injection via share links
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Sanitize text content decoded from a share link.
 * Fabric.js renders text via canvas draw calls (no DOM injection risk),
 * but strip HTML/script tags anyway as defense-in-depth.
 */
function sanitizeText(text) {
  if (typeof text !== 'string') return '';
  return text
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]+>/g, '')
    .substring(0, 500); // cap at 500 chars per field
}

function sanitizeState(state) {
  // Sanitize all text edits
  if (state.textEdits) {
    for (const [id, text] of Object.entries(state.textEdits)) {
      state.textEdits[id] = sanitizeText(text);
    }
  }

  // Sanitize wordmark text
  if (state.wordmark?.text) {
    state.wordmark.text = sanitizeText(state.wordmark.text);
  }

  // Sanitize user text objects
  if (state.userTextObjects) {
    for (const uto of state.userTextObjects) {
      if (uto.text) uto.text = sanitizeText(uto.text);
      if (uto.content) uto.content = sanitizeText(uto.content);
    }
  }

  // Drop any unknown fields (forward compatibility — silently ignore)
  const known = new Set([
    'template', 'scheme', 'size', 'overlays', 'textEdits', 'textStyleOverrides',
    'groupAlignOverrides', 'wordmark', 'mangleSeed', 'mangleParams',
    'eventDateISO', 'dateFormat', 'eventTime', 'userTextObjects', 'ref',
  ]);
  for (const key of Object.keys(state)) {
    if (!known.has(key)) delete state[key];
  }

  return state;
}

// ─────────────────────────────────────────────────────────────────────────────
// PUBLIC API
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build the shareable state payload from current editor state.
 *
 * @param {object} params
 * @param {object} params.currentState - the editor's currentState object
 * @param {object} params.fabricCanvas - the Fabric.js canvas
 * @param {string} params.currentSize - current TEMPLATE_SIZES key
 * @param {function} params.captureTextEdits - function that returns {id: text} map
 * @param {object} params.textStyleOverrides - per-object style overrides
 * @param {object} params.groupAlignOverrides - per-group alignment overrides
 * @param {Array} params.userTextObjects - user-created text objects
 * @param {string|null} params.userId - authenticated user ID (null if anon)
 * @param {number} params.mangleSeed - current mangle seed
 * @returns {object} payload ready for encoding
 */
export function buildSharePayload({
  currentState,
  captureTextEdits,
  textStyleOverrides,
  groupAlignOverrides,
  userTextObjects,
  userId,
  mangleSeed,
  currentSize,
}) {
  return {
    template: currentState.template,
    scheme: currentState.scheme?.name || 'dark',
    size: currentSize,
    overlays: currentState.overlays,
    textEdits: captureTextEdits(),
    textStyleOverrides: textStyleOverrides || {},
    groupAlignOverrides: groupAlignOverrides || {},
    wordmark: currentState._wordmarkText ? {
      text: currentState._wordmarkText,
      font: currentState._wordmarkFont,
      color: currentState._wordmarkColor,
      bold: currentState._wordmarkBold,
      italic: currentState._wordmarkItalic,
      uppercase: currentState._wordmarkUppercase,
      accent: currentState._wordmarkAccent,
    } : null,
    mangleSeed: mangleSeed || 0,
    mangleParams: currentState._mangleParams,
    eventDateISO: currentState.eventDateISO,
    dateFormat: currentState.dateFormat,
    eventTime: currentState.eventTime,
    userTextObjects: userTextObjects || [],
    ref: userId || getAnonRefId(),
  };
}

/**
 * Encode a share payload into a URL fragment string.
 *
 * @param {object} payload - from buildSharePayload()
 * @returns {Promise<{url: string, length: number, warning: string|null, error: string|null}>}
 */
export async function encodeShareLink(payload) {
  const stripped = stripDefaults(payload);
  const json = JSON.stringify(stripped);
  const encoded = await compressToBase64url(json);
  const fragment = `${SHARE_PREFIX}${SHARE_VERSION}.${encoded}`;
  const fullUrl = `${window.location.origin}/${fragment}`;

  const result = {
    url: fullUrl,
    fragment,
    length: fullUrl.length,
    warning: null,
    error: null,
  };

  if (fullUrl.length > MAX_URL_HARD) {
    result.error = `This design is too complex to share as a link (${fullUrl.length} characters). Try simplifying the design or removing custom text objects.`;
  } else if (fullUrl.length > MAX_URL_WARN) {
    result.warning = `This link is ${fullUrl.length} characters — it may not work in all messaging apps. Consider shortening text content.`;
  }

  return result;
}

/**
 * Check if the current URL hash contains a share link.
 * @returns {boolean}
 */
export function hasShareLink() {
  return window.location.hash.startsWith(SHARE_PREFIX + SHARE_VERSION + '.');
}

/**
 * Decode a share link from the URL hash and return the design state.
 * Returns null if no valid share link is present.
 *
 * @returns {Promise<{state: object, ref: string|null}|null>}
 */
export async function decodeShareLink() {
  const hash = window.location.hash;
  if (!hash.startsWith(SHARE_PREFIX + SHARE_VERSION + '.')) return null;

  try {
    // Extract the encoded payload after "v1."
    const encoded = hash.slice(SHARE_PREFIX.length + SHARE_VERSION.length + 1);
    const json = await decompressFromBase64url(encoded);
    const stripped = JSON.parse(json);
    const state = expandDefaults(stripped);
    const sanitized = sanitizeState(state);

    // Extract referral ID before returning
    const ref = sanitized.ref || null;

    return { state: sanitized, ref };
  } catch (err) {
    console.error('Failed to decode share link:', err);
    return null;
  }
}

/**
 * Clear the share link from the URL without triggering a page reload.
 * Called after successfully hydrating a shared design.
 */
export function clearShareHash() {
  if (window.location.hash.startsWith(SHARE_PREFIX)) {
    history.replaceState(null, '', window.location.pathname);
  }
}

export { MAX_URL_WARN, MAX_URL_HARD, getAnonRefId };
